/* Copyright (c) 2017 Backpack Health LLC */

import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import * as _ from 'lodash';

import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';

import ContentWrapper from '../../../components/ContentWrapper';
import ProfileRaceSelfIdentity from './ProfileRaceSelfIdentity';

import { LocalizedListItem, Race } from '../../../../apiServices/ListsService';
import { LocalizedProfileRace } from '../../../../app/localization/LocalizedRace';
import ProfileModel, { ProfileData } from '../../../../models/ProfileModel';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { ProfileContentProps } from './ProfileContentProps';
import { withIntl } from '../../../../utilities/localizedUtil';
import { ConnectedGrayPrivacyCard } from '../../../components/card/PrivacyCard';
import ContentFrame from '../../../applicationFrame/ContentFrame';
import { PolicyName } from '../../../../models/PolicyModel';
import { Card, CardRowUI } from '../../../../bphComponents/bundle';
import COLORS from '../../../styles/Colors';

interface Props extends ProfileContentProps {
  races: Race[];
  requestInitialData(): void;
  profileData: ProfileData;
  isMainProfile: boolean;
  onChangeProfile(data: ProfileData, callback): void;
  profileIsLoaded: boolean;
  goBack(): void;
  clearViewErrors(): void;
  updateProfile(data, callback): any;
  onWithdrawConsent(profileId: number, policyIds: number[]): void;
  onProvideConsent(profileId: number, policyIds: number[]): void;
  policyIds: number[];
  intl: any;
}

interface State {
  clickedId?: number;
  isRaceExpanded: boolean;
}

interface EditableProfileProps {
  profile: ProfileModel;
}

interface EditableProfileState {
  profileData: ProfileData;
}

function withEditableProfile(Component) {
  return class EditableProfile extends React.Component<EditableProfileProps, EditableProfileState> {
    state: EditableProfileState = {
      profileData: _.assign({}, this.props.profile.data),
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (!this.state.profileData.id) {
        this.setState({
          profileData: _.assign({}, nextProps.profile.data),
        });
      }
    }

    onChangeProfile = (profileData, callback) => {
      this.setState({ profileData: _.assign({}, profileData) }, callback);
    }

    render() {
      return (
        <Component
          {...this.props}
          profileData={this.state.profileData}
          onChangeProfile={this.onChangeProfile}
          profileIsLoaded={!!this.state.profileData.id}
        />
      );
    }
  };
}

@withIntl
class ProfileRace extends React.Component<Props, State> {

  hasSelectedRace = () => {
    const { profileData } = this.props;
    const selectedRaces = profileData.race && profileData.race.race_id || [];
    return  selectedRaces.length > 0;
  }

  state: State = {
    isRaceExpanded: !this.hasSelectedRace(),
  };

  viewIsValid = () => {
    const profile = new ProfileModel(this.props.profileData);
    const validators = [() => profile.hasValidRace()];

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        return false;
      }
    }

    return true;
  }

  onCancel = () => {
    this.props.goBack();
  }

  onSubmit = (e) => {
    if (e) { e.preventDefault(); }
    if (!this.viewIsValid()) { return; }

    const profileWithValidValues = _.pickBy(this.props.profileData, (val) => val !== null);

    this.props.onProvideConsent(profileWithValidValues.id, this.props.policyIds);

    this.props.updateProfile(profileWithValidValues, (apiError) => {
      if (!apiError) {
        this.props.goBack();
      }
    });
  }

  onNext = () => {
    this.setState({ isRaceExpanded: false });
  }

  render() {
    const { isRaceExpanded } = this.state;
    const { profileIsLoaded, profileData, races, intl, isMainProfile, onChangeProfile, clearViewErrors } = this.props;

    if (!profileIsLoaded) { return null; }

    const profile = new ProfileModel(profileData);
    const hasRacesSelected = this.hasSelectedRace();

    const phraseData = ProfilePhrases.profileRace();
    const greetingName = profile.greetingName();

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            submitDisabled={!this.viewIsValid()}
            cancel={this.onCancel}
            submit={isRaceExpanded ? null : this.onSubmit}
            next={isRaceExpanded ? this.onNext : null}
            color="white"
          />

          <ContentFrame>
            <ScrollAwareScroller>
              <AppHeaderScrollableText
                id={phraseData.title.id}
                values={{ string1: greetingName }}
              />

              <ContentWrapper>

                <ConnectedGrayPrivacyCard
                  name={PolicyName.RACE}
                  subTitle="privacy.privacyBlock.race.teaser"
                />

                <Card.CardShape>
                  <Card.CollapsibleSummaryWithControl
                    label={(
                      <FormattedMessage id="race.selectionInstructions.text" />
                    )}
                    background={COLORS.gsLightest}
                    disableCollapseUntilValue={true}
                    hasBottomBorder={true}
                    value={(
                      hasRacesSelected
                      ? <div className="font-color-profile-accent">
                        <LocalizedProfileRace
                          profile={new ProfileModel(profileData)}
                          raceCollection={races}
                          intl={intl}
                        />
                        </div>
                      : null
                    )}
                    isExpanded={this.state.isRaceExpanded}
                    isMultiLine={false}
                    onClick={() => {
                      if (!hasRacesSelected) { return; }
                      this.setState({
                        isRaceExpanded: !this.state.isRaceExpanded,
                      });
                    }}
                  >
                    {this.renderRacesItems()}
                  </Card.CollapsibleSummaryWithControl>
                </Card.CardShape>

                {
                  hasRacesSelected && !isRaceExpanded ? (
                    <Card.CardShape>
                      <ProfileRaceSelfIdentity
                        profileData={profileData}
                        races={races}
                        phraseData={ProfilePhrases.profileRaceSelfIdentity(isMainProfile)}
                        onChange={onChangeProfile}
                        clearViewErrors={clearViewErrors}
                      />
                    </Card.CardShape>
                  ) : (
                    <Card.ExternalCardButtonLink onClick={this.decline}>
                      <FormattedMessage id={phraseData.decline.id}/>
                    </Card.ExternalCardButtonLink>
                  )
                }
              </ContentWrapper>

            </ScrollAwareScroller>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }

  renderRacesItems() {
    const { profileData, intl } = this.props;
    const phraseData = ProfilePhrases.profileRace();
    const races = LocalizedListItem.wrap(this.props.races, intl);
    const selectedRaces = profileData.race && profileData.race.race_id || [];

    const listItems = [];

    for (let i = 0; i < races.length; i ++) {
      const indigenousRaces = [];

      while (i < races.length && races[i].item.indigenous) {
        indigenousRaces.push(races[i ++]);
      }

      if (indigenousRaces.length) {
        listItems.push(
          <IndigenousBlock
            key={`indigenous-${races[i].item.id}`}
            races={indigenousRaces}
            selectedRaces={selectedRaces}
            toggleRace={this.toggleRace}
            label={phraseData.indigenous.id}
          />,
        );
      }
      listItems.push(
        <RaceItem
          key={races[i].item.id}
          race={races[i]}
          selectedRaces={selectedRaces}
          toggleRace={this.toggleRace}
          isNested={false}
        />,
      );
    }

    return listItems;
  }

  toggleRace = (raceId: number) => {
    const { profileData, onChangeProfile } = this.props;
    const selectedRaces = [...(profileData.race && profileData.race.race_id || [])];

    const idx = selectedRaces.indexOf(raceId);

    if (idx >= 0) {
      selectedRaces.splice(idx, 1);
    } else {
      selectedRaces.push(raceId);
    }

    const selected = selectedRaces.length > 0;

    onChangeProfile(
      {
        ...profileData,
        race: {
          identity: profileData.race && profileData.race.identity || undefined,
          race_id: selected ? selectedRaces : undefined,
        },
        race_selection: selected ? 'selected' : undefined,
      },
      () => {},
    );
  }

  decline = () => {
    const { profileData, policyIds } = this.props;

    const data = {
      ...profileData,
      race: undefined,
      race_selection: 'no_answer',
    };

    this.props.onWithdrawConsent(profileData.id, policyIds);
    this.props.updateProfile(data, (apiError) => {
      if (!apiError) {
        this.props.goBack();
      }
    });
  }

  UNSAFE_componentWillMount() {
    this.props.requestInitialData();

    const { profileData, onChangeProfile } = this.props;

    // reset no_answer selection
    if (profileData.race_selection === 'no_answer') {
      onChangeProfile(
        {
          ...profileData,
          race: {
            race_id: [],
          },
          race_selection: 'selected',
        },
        () => {},
      );
    }
  }
}

const RaceItem = ({ race, selectedRaces, toggleRace, isNested }: { race: LocalizedListItem<Race>, selectedRaces: number[], toggleRace, isNested: boolean }) => {
  const selected = selectedRaces.indexOf(race.item.id) >= 0;

  return (
    <CardRowUI.SelectableRow
      isSelected={selected}
      selectedClassName="fill-color-profile-highlight"
      hasBottomBorder={true}
      onClick={() => toggleRace(race.item.id)}
    >
      {race.selectName}
    </CardRowUI.SelectableRow>
  );
};

const IndigenousBlock = ({ races, selectedRaces, toggleRace, label }: { races: LocalizedListItem<Race>[], selectedRaces: number[], toggleRace, label }) => {
  const selectedIndigenous = races
    .filter(race => race.item.indigenous)
    .filter(race => selectedRaces.indexOf(race.item.id) >= 0);

  const [isExpanded, setIsExpanded] = useState(selectedIndigenous.length > 0);

  return (
    <>
    <Card.CardContent hasBottomBorder={true} onClick={() => { setIsExpanded(!isExpanded); }}>
      <Card.CollapsibleControl isExpanded={isExpanded}>
        <FormattedMessage id={label}/>
      </Card.CollapsibleControl>
    </Card.CardContent>

    <Card.CollapsibleContent isExpanded={isExpanded}>
      {
        races.map(race => (
          <RaceItem
            key={race.item.id}
            race={race}
            selectedRaces={selectedRaces}
            toggleRace={toggleRace}
            isNested={true}
          />
        ))
      }
    </Card.CollapsibleContent>
    </>
  );
};

export default withEditableProfile(ProfileRace);
